import { useCallback, useEffect, useRef, useState } from "react";

export interface NotificationRecipient {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string | null;
}

export interface NotificationTicket {
  id?: string;
  ticket_number?: number;
  name?: string;
  [key: string]: unknown;
}

export interface NotificationTaskItem {
  id?: string;
  ticket_item_number?: number;
}

export interface NotificationItem {
  id?: string;
  created_at?: string;
  sender?: NotificationRecipient;
  ticket?: NotificationTicket;
  ticket_item?: NotificationTaskItem | null;
  message?: string;
  notification_type?: string;
  read_at?: string | null;
}

export interface NotificationsSnapshot {
  notifications: NotificationItem[];
  unreadCount: number;
}

export interface NotificationsApiAdapter {
  fetchNotifications(params: {
    ordering: string;
    perPage: number;
  }): Promise<{ data: { results?: NotificationItem[] } }>;
  fetchUnreadCount(): Promise<number>;
  markAsRead(id: string): Promise<unknown>;
  markAllAsRead(id: string): Promise<unknown>;
  deleteNotification(id: string): Promise<unknown>;
}

const POLL_INTERVAL_MS = 30_000;
const FETCH_LIMIT = 20;

interface AdapterCacheEntry {
  snapshot: NotificationsSnapshot | null;
  fetchedAt: number;
  inFlight: Promise<NotificationsSnapshot> | null;
  subscribers: Set<(snapshot: NotificationsSnapshot) => void>;
}

// The bell lives inside AppLayout, which every page renders for itself, so it
// unmounts and remounts on each navigation. Caching outside the component keeps
// a route change from refetching data that is still fresh, and sharing the
// in-flight promise collapses concurrent mounts - including the double mount
// React StrictMode performs in development - into a single request.
const adapterCaches = new WeakMap<NotificationsApiAdapter, AdapterCacheEntry>();

function getAdapterCache(api: NotificationsApiAdapter): AdapterCacheEntry {
  let entry = adapterCaches.get(api);
  if (!entry) {
    entry = { snapshot: null, fetchedAt: 0, inFlight: null, subscribers: new Set() };
    adapterCaches.set(api, entry);
  }
  return entry;
}

// Several components can read the same adapter at once - the header bell and the
// sidebar badge both do - and each holds its own state. Every cache write is
// broadcast so they cannot drift apart when one of them marks something read
// without a navigation to remount the others.
function writeCachedSnapshot(
  api: NotificationsApiAdapter,
  snapshot: NotificationsSnapshot
) {
  const cache = getAdapterCache(api);
  cache.snapshot = snapshot;
  for (const notifySubscriber of cache.subscribers) {
    notifySubscriber(snapshot);
  }
}

function loadNotifications(
  api: NotificationsApiAdapter,
  { force }: { force: boolean }
): Promise<NotificationsSnapshot> {
  const cache = getAdapterCache(api);

  if (cache.inFlight) return cache.inFlight;
  if (!force && cache.snapshot && Date.now() - cache.fetchedAt < POLL_INTERVAL_MS) {
    return Promise.resolve(cache.snapshot);
  }

  // The badge counts every unread notification, not just the FETCH_LIMIT most
  // recent ones the panel displays, so the count comes from the server.
  const request = Promise.all([
    api.fetchNotifications({ ordering: "-created_at", perPage: FETCH_LIMIT }),
    api.fetchUnreadCount(),
  ])
    .then(([response, unreadCount]) => {
      const snapshot = {
        notifications: response.data.results ?? [],
        unreadCount,
      };
      cache.fetchedAt = Date.now();
      writeCachedSnapshot(api, snapshot);
      return snapshot;
    })
    .finally(() => {
      cache.inFlight = null;
    });

  cache.inFlight = request;
  return request;
}

function patchCachedSnapshot(
  api: NotificationsApiAdapter,
  snapshot: NotificationsSnapshot
) {
  if (getAdapterCache(api).snapshot) {
    writeCachedSnapshot(api, snapshot);
  }
}

export interface UseNotificationsReturn {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: boolean;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  refetch: () => void;
}

export function useNotifications(api: NotificationsApiAdapter): UseNotificationsReturn {
  const cachedSnapshot = getAdapterCache(api).snapshot;
  const [notifications, setNotifications] = useState<NotificationItem[]>(
    cachedSnapshot?.notifications ?? []
  );
  const [unreadCount, setUnreadCount] = useState(cachedSnapshot?.unreadCount ?? 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetch = useCallback(
    async (options?: { force?: boolean }) => {
      try {
        const snapshot = await loadNotifications(api, {
          force: options?.force ?? true,
        });
        if (isMountedRef.current) {
          setNotifications(snapshot.notifications);
          setUnreadCount(snapshot.unreadCount);
          setError(false);
        }
      } catch {
        if (isMountedRef.current) {
          setError(true);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [api]
  );

  const startPolling = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!document.hidden) {
        fetch({ force: true });
      }
    }, POLL_INTERVAL_MS);
  }, [fetch]);

  useEffect(() => {
    const cache = getAdapterCache(api);
    const applyBroadcastSnapshot = (snapshot: NotificationsSnapshot) => {
      setNotifications(snapshot.notifications);
      setUnreadCount(snapshot.unreadCount);
    };
    cache.subscribers.add(applyBroadcastSnapshot);
    return () => {
      cache.subscribers.delete(applyBroadcastSnapshot);
    };
  }, [api]);

  useEffect(() => {
    isMountedRef.current = true;
    // A remount from navigating between pages reuses whatever the cache still
    // considers fresh; only the poll and returning to the tab force the network.
    setLoading(getAdapterCache(api).snapshot === null);
    fetch({ force: false });
    startPolling();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetch({ force: true });
        startPolling();
      } else {
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [api, fetch, startPolling]);

  // Optimistic updates write through to the cache too, so navigating away and
  // back does not resurrect notifications the user just read or deleted.
  const applyOptimisticUpdate = useCallback(
    (snapshot: NotificationsSnapshot, revertTo: NotificationsSnapshot) => {
      setNotifications(snapshot.notifications);
      setUnreadCount(snapshot.unreadCount);
      patchCachedSnapshot(api, snapshot);
      return () => {
        patchCachedSnapshot(api, revertTo);
        if (isMountedRef.current) {
          setNotifications(revertTo.notifications);
          setUnreadCount(revertTo.unreadCount);
        }
      };
    },
    [api]
  );

  const markAsRead = useCallback(
    (id: string) => {
      const previous = { notifications, unreadCount };
      const wasUnread = !notifications.find((n) => n.id === id)?.read_at;
      const now = new Date().toISOString();
      const revert = applyOptimisticUpdate(
        {
          notifications: notifications.map((n) =>
            n.id === id ? { ...n, read_at: now } : n
          ),
          unreadCount: wasUnread ? Math.max(0, unreadCount - 1) : unreadCount,
        },
        previous
      );
      api.markAsRead(id).catch(revert);
    },
    [api, applyOptimisticUpdate, notifications, unreadCount]
  );

  const markAllAsRead = useCallback(() => {
    if (notifications.length === 0) return;
    const firstId = notifications[0].id;
    if (!firstId) return;

    const now = new Date().toISOString();
    const revert = applyOptimisticUpdate(
      {
        notifications: notifications.map((n) => ({ ...n, read_at: n.read_at ?? now })),
        unreadCount: 0,
      },
      { notifications, unreadCount }
    );
    api.markAllAsRead(firstId).catch(revert);
  }, [api, applyOptimisticUpdate, notifications, unreadCount]);

  const deleteNotification = useCallback(
    (id: string) => {
      const wasUnread = !notifications.find((n) => n.id === id)?.read_at;
      const revert = applyOptimisticUpdate(
        {
          notifications: notifications.filter((n) => n.id !== id),
          unreadCount: wasUnread ? Math.max(0, unreadCount - 1) : unreadCount,
        },
        { notifications, unreadCount }
      );
      api.deleteNotification(id).catch(revert);
    },
    [api, applyOptimisticUpdate, notifications, unreadCount]
  );

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetch,
  };
}
